import React, { useContext, useState, useEffect } from "react";
import styled, { ThemeContext } from "styled-components";
import { ProductionDetails } from "components/shared";
import { Grid } from "semantic-ui-react";
import PropTypes from "prop-types";
import moment from "moment";
import useAxios from "axios-hooks";
import "flag-icon-css/css/flag-icon.css";

const DetailedProductionRows = styled((props) => <Grid.Row {...props} />)`
  margin-top: ${(props) => (props.fulldetails ? "0.5rem" : undefined)};
  &:nth-child(1) {
    margin-top: 1rem;
  }
`;

const GridMobileRow = styled((props) => <Grid.Row {...props} />)`
  row-gap: ${(props) => props.rowgap};
`;

const CardFooter = ({
  fullDetails,
  productions,
  children,
  windTurbine,
  turbineParams,
  liveProps,
}) => {
  const themeContext = useContext(ThemeContext);
  const [reportData, setReportData] = useState();
  const [todayProduction, setTodayProduction] = useState();
  const [thisMonthProduction, setThisMonthProduction] = useState();
  const [thisYearProduction, setThisYearProduction] = useState();
  const [totalProduction, setTotalProduction] = useState();
  const [avgWindToday, setAvgWindToday] = useState();
  const [avgWindThisMonth, setAvgWindThisMonth] = useState();
  const [avgPowerToday, setAvgPowerToday] = useState();
  const [avgPowerThisMonth, setAvgPowerThisMonth] = useState();

  const [{ data, loading }, getData] = useAxios(
    {
      url: `/productionData/summary/${windTurbine.id}`,
    },
    {
      useCache: true,
      manual: true,
    }
  );

  const [reportResponse, getReport] = useAxios(
    {
      url: `/productionData/report/${windTurbine.id}`,
    },
    {
      useCache: true,
      manual: true,
    }
  );

  useEffect(() => {
    if (fullDetails) {
      if (reportResponse.data) {
        setReportData(reportResponse.data);
      } else {
        if (!reportResponse.loading) {
          getReport();
        }
      }
    } else {
      if (data) {
        setReportData(data);
      } else {
        if (!loading) {
          getData();
        }
      }
    }
  }, [reportResponse, data]);

  function getParamByKey(turbineParams, key) {
    return turbineParams.find(
      (p) => p.dbname && p.dbname.toLowerCase() === key.toLowerCase()
    );
  }

  useEffect(() => {
    function getProductionValuesByKey(key) {
      if (!reportData || !reportData.length > 0) return 0;
      const info = reportData.find((d) => d.type === key);
      if (!info) return 0;
      return info.value;
    }

    if (reportData && turbineParams && turbineParams.length > 0) {
      // A schema-driven turbine's registers aren't guaranteed to have a
      // dbname of exactly "wind"/"power" (unlike the legacy per-type xlsx's
      // fixed Monitoring sheet) - the Live graph already handles this by
      // matching on category instead (see selectors/turbineParams.js), but
      // this lookup is still by exact dbname, so it can come back
      // undefined here. Guarded the same way the kwh_meter lookup below
      // already is, instead of reading .scale off it unconditionally and
      // throwing - which had no error boundary above it to catch, so it
      // took the entire dashboard down on login for any account with such
      // a turbine.
      const windParam = getParamByKey(turbineParams, "wind");
      const powerParam = getParamByKey(turbineParams, "power");
      const windScale = (windParam && windParam.scale) || 1;
      const powerScale = (powerParam && powerParam.scale) || 1;
      setAvgPowerToday(getProductionValuesByKey("todayAvgPower") / powerScale);
      setAvgWindToday(getProductionValuesByKey("todayAvgWind") / windScale);
      setAvgPowerThisMonth(
        getProductionValuesByKey("thisMonthAvgPower") / powerScale
      );
      setAvgWindThisMonth(
        getProductionValuesByKey("thisMonthAvgWind") / windScale
      );

      const kwhParam = getParamByKey(turbineParams, "kwh_meter");
      const scale = kwhParam && kwhParam.scale ? kwhParam.scale : 1;
      const todayProd = getProductionValuesByKey("startToday");
      const thisMonthProd = getProductionValuesByKey("startThisMonth");
      const thisYearProd = getProductionValuesByKey("startThisYear");
      const latestLog = getProductionValuesByKey("latestLog");

      setTodayProduction(todayProd / scale);
      setThisMonthProduction(thisMonthProd / scale);
      setThisYearProduction(thisYearProd / scale);
      setTotalProduction(latestLog / scale);
    }
  }, [reportData, turbineParams]);

  // Vestas turbines publish this month's production directly
  // (energy_month, in MWh) - more accurate and immediate than the month
  // delta above, which is computed from kwh_meter history.
  //
  // "Produced today" deliberately does NOT use the board's energy_today: it
  // is derived on the board from the first reading after boot/midnight, so
  // any reboot or outage during the day leaves it far too small (field-
  // confirmed 2026-09-19: 0.001 MWh at night for a turbine with 24 MWh
  // this month). The server computes today as latest kwh_meter minus the
  // last kwh_meter of the previous day (see productionData.js
  // getDailyProduction), which only depends on the cumulative total.
  //
  // Computed directly at render time rather than via a second setState
  // effect - that was tried first and lost a race against the effect
  // above: getTurbineParamsDefinitionByKey (turbineParams) returns a brand
  // new array on every single call, so its effect re-fires and stomps this
  // one on every re-render regardless of order, since a plain [liveProps.
  // energy_today] dependency doesn't change most of those times. Deriving
  // the displayed value inline sidesteps effect ordering entirely. Only
  // takes effect when the turbine actually sends these two dbnames - every
  // other schema leaves liveProps.energy_today/energy_month undefined, so
  // this has no effect for them.
  const displayedTodayProduction = todayProduction;
  const displayedThisMonthProduction =
    typeof liveProps.energy_month === "number" ? liveProps.energy_month * 1000 : thisMonthProduction;
  // Same for the year (PF2 V0.9.41 energy_year, MWh, sum of the turbine's
  // own monthly counters) - the server's kwh_meter delta only covers the
  // time since the turbine started publishing kwh_meter. PF2 only sends it
  // once all 12 months have been read, so until then this falls back.
  const displayedThisYearProduction =
    typeof liveProps.energy_year === "number" ? liveProps.energy_year * 1000 : thisYearProduction;

  function formatNumber(number) {
    return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(
      number
    );
  }

  // Total/monthly production can reach millions of kWh (this turbine's
  // total is already ~11.46 million) - switches to MWh above 10,000 kWh so
  // the number stays a reasonable length instead of overflowing the card.
  const ENERGY_MWH_THRESHOLD_KWH = 10000;
  function formatEnergyValue(kwhValue) {
    if (typeof kwhValue !== "number" || Number.isNaN(kwhValue)) return formatNumber(kwhValue);
    if (Math.abs(kwhValue) >= ENERGY_MWH_THRESHOLD_KWH) {
      return formatNumber(kwhValue / 1000);
    }
    return formatNumber(kwhValue);
  }
  function formatEnergyUnit(kwhValue) {
    return typeof kwhValue === "number" && Math.abs(kwhValue) >= ENERGY_MWH_THRESHOLD_KWH ? "MWh" : "kWH";
  }
  function formatEnergyDetails(kwhValue) {
    return `${formatEnergyValue(kwhValue)} ${formatEnergyUnit(kwhValue)}`;
  }

  function renderDailyProduction() {
    const maxPower = windTurbine.maxPower || 60;
    const maxValue = maxPower * 24;
    return (
      <ProductionDetails
        key="daily"
        value={formatEnergyValue(displayedTodayProduction)}
        maxValue={maxValue}
        title="Today"
        details={formatEnergyDetails(displayedTodayProduction)}
        color={themeContext.primaryColor}
      />
    );
  }

  function renderMonthlyProduction() {
    const maxPower = windTurbine.maxPower || 60;
    const maxValue = maxPower * 24 * moment(Date.now()).daysInMonth();
    return (
      <ProductionDetails
        key="monthly"
        value={formatEnergyValue(displayedThisMonthProduction)}
        maxValue={maxValue}
        title="Month"
        details={formatEnergyDetails(displayedThisMonthProduction)}
        color={themeContext.primaryColor}
      />
    );
  }

  function renderYearlyProduction() {
    const maxPower = windTurbine.maxPower || 60;
    const maxValue = maxPower * 24 * moment(Date.now()).daysInMonth() * 12;
    return (
      <ProductionDetails
        key="yearly"
        value={formatEnergyValue(displayedThisYearProduction)}
        maxValue={maxValue}
        title="Year"
        details={formatEnergyDetails(displayedThisYearProduction)}
        color={themeContext.primaryColor}
      />
    );
  }

  function renderTotalProduction() {
    const maxPower = windTurbine.maxPower || 60;
    const maxValue = maxPower * 24 * moment(Date.now()).daysInMonth() * 12 * 10;
    return (
      <ProductionDetails
        key="total"
        value={formatEnergyValue(totalProduction)}
        maxValue={maxValue}
        title="Total"
        details={formatEnergyDetails(totalProduction)}
        color={themeContext.primaryColor}
      />
    );
  }

  function renderAvgPowerDaily() {
    return (
      <ProductionDetails
        key="avg-daily"
        value={moment(Date.now()).hours()}
        maxValue={24}
        title="Avg. power today"
        details={`${formatNumber(avgPowerToday)} kW`}
        withProgress={false}
      />
    );
  }

  function renderAvgPowerMonthly() {
    return (
      <ProductionDetails
        key="avg-monthly"
        value={moment(Date.now()).date()}
        maxValue={moment(Date.now()).daysInMonth()}
        title="Avg. power this month"
        details={`${formatNumber(avgPowerThisMonth)} kW`}
        withProgress={false}
      />
    );
  }

  function renderAvgWindDaily() {
    return (
      <ProductionDetails
        key="avg-wind-daily"
        value={moment(Date.now()).hours()}
        maxValue={24}
        title="Avg. wind today"
        details={`${formatNumber(avgWindToday)} m/s`}
        withProgress={false}
      />
    );
  }

  function renderAvgWindMonthly() {
    return (
      <ProductionDetails
        key="avg-wind-month"
        value={moment(Date.now()).date()}
        maxValue={moment(Date.now()).daysInMonth()}
        title="Avg. wind this month"
        details={`${formatNumber(avgWindThisMonth)} m/s`}
        withProgress={false}
      />
    );
  }

  const renderDashboardFooterProductionValues = () => {
    return (
      <Grid columns={4} stretched centered>
        <Grid.Column>{renderDailyProduction()}</Grid.Column>
        <Grid.Column>{renderMonthlyProduction()}</Grid.Column>
        <Grid.Column>{renderYearlyProduction()}</Grid.Column>
        <Grid.Column>{renderTotalProduction()}</Grid.Column>
      </Grid>
    );
  };

  const renderDashboardDetailsInsideCard = () => {
    return (
      <div className="mobile hidden">
        <Grid style={{ padding: "1rem" }}>
          <DetailedProductionRows fulldetails={fullDetails ? 1 : 0}>
            <Grid.Column style={{ flex: 1 }}>
              {renderDailyProduction()}
            </Grid.Column>
            <Grid.Column style={{ flex: 1 }}>
              {renderMonthlyProduction()}
            </Grid.Column>
            <Grid.Column style={{ flex: 1 }}>
              {renderYearlyProduction()}
            </Grid.Column>
            <Grid.Column style={{ flex: 1 }}>
              {renderTotalProduction()}
            </Grid.Column>
          </DetailedProductionRows>
          <DetailedProductionRows fulldetails={fullDetails ? 1 : 0}>
            <Grid.Column style={{ flex: 1 }}>
              {renderAvgPowerDaily()}
            </Grid.Column>
            <Grid.Column style={{ flex: 1 }}>
              {renderAvgPowerMonthly()}
            </Grid.Column>
            <Grid.Column style={{ flex: 1 }}>
              {renderAvgWindDaily()}
            </Grid.Column>
            <Grid.Column style={{ flex: 1 }}>
              {renderAvgWindMonthly()}
            </Grid.Column>
          </DetailedProductionRows>
        </Grid>
      </div>
    );
  };

  const renderDashboardDetailsInsideCardMobile = () => {
    return (
      <div className="mobile only">
        <Grid columns={2} rows={4} style={{ padding: "1rem" }}>
          <GridMobileRow>
            <Grid.Column>{renderDailyProduction()}</Grid.Column>
            <Grid.Column>{renderMonthlyProduction()}</Grid.Column>
          </GridMobileRow>
          <GridMobileRow>
            <Grid.Column>{renderYearlyProduction()}</Grid.Column>
            <Grid.Column>{renderTotalProduction()}</Grid.Column>
          </GridMobileRow>
          <GridMobileRow rowgap="2rem">
            <Grid.Column>{renderAvgPowerDaily()}</Grid.Column>
            <Grid.Column>{renderAvgPowerMonthly()}</Grid.Column>
            <Grid.Column>{renderAvgWindDaily()}</Grid.Column>
            <Grid.Column>{renderAvgWindMonthly()}</Grid.Column>
          </GridMobileRow>
        </Grid>
      </div>
    );
  };

  const renderEnergyProductions = () => {
    return fullDetails ? (
      <div>
        {renderDashboardDetailsInsideCard()}
        {renderDashboardDetailsInsideCardMobile()}
      </div>
    ) : (
      renderDashboardFooterProductionValues()
    );
  };

  return (
    <>
      {renderEnergyProductions()}
      {children}
    </>
  );
};

CardFooter.propTypes = {
  fullDetails: PropTypes.bool,
  productions: PropTypes.arrayOf(PropTypes.shape({})),
  windTurbine: PropTypes.object.isRequired,
  turbineParams: PropTypes.array,
  liveProps: PropTypes.object,
};

CardFooter.defaultProps = {
  fullDetails: false,
  productions: [],
  turbineParams: [],
  liveProps: {},
};

export default React.memo(CardFooter);
